import type { Category, Instance } from './catalog'
import type { NodeFit } from './fit'

/** Price per hour → per interval. A month is 730 hours, as AWS, Azure and GCP bill it. */
export const INTERVAL_HOURS = { hour: 1, day: 24, week: 168, month: 730 } as const
export type Interval = keyof typeof INTERVAL_HOURS
export const INTERVALS = Object.keys(INTERVAL_HOURS) as Interval[]

export const isInterval = (value: unknown): value is Interval =>
  INTERVALS.includes(value as Interval)

export const SORT_ORDERS = [
  'cost-per-pod-asc',
  'cost-per-pod-desc',
  'name',
  'cpu-asc',
  'cpu-desc',
  'memory-asc',
  'memory-desc',
  'pods-desc',
] as const
export type SortOrder = (typeof SORT_ORDERS)[number]

export const isSortOrder = (value: unknown): value is SortOrder =>
  SORT_ORDERS.includes(value as SortOrder)

const byName = (a: NodeFit, b: NodeFit): number =>
  a.instance.name.localeCompare(b.instance.name, 'en', { numeric: true })

const COMPARATORS: Record<SortOrder, (a: NodeFit, b: NodeFit) => number> = {
  // Instances where nothing fits cost Infinity per pod and sink to the bottom.
  'cost-per-pod-asc': (a, b) =>
    a.costPerPod - b.costPerPod || a.instance.hourlyPrice - b.instance.hourlyPrice,
  'cost-per-pod-desc': (a, b) =>
    (Number.isFinite(b.costPerPod) ? b.costPerPod : -1) -
    (Number.isFinite(a.costPerPod) ? a.costPerPod : -1),
  name: byName,
  'cpu-asc': (a, b) =>
    a.instance.vcpu - b.instance.vcpu || a.instance.memoryGiB - b.instance.memoryGiB,
  'cpu-desc': (a, b) =>
    b.instance.vcpu - a.instance.vcpu || b.instance.memoryGiB - a.instance.memoryGiB,
  'memory-asc': (a, b) =>
    a.instance.memoryGiB - b.instance.memoryGiB || a.instance.vcpu - b.instance.vcpu,
  'memory-desc': (a, b) =>
    b.instance.memoryGiB - a.instance.memoryGiB || b.instance.vcpu - a.instance.vcpu,
  'pods-desc': (a, b) => b.pods - a.pods || a.costPerPod - b.costPerPod,
}

/** Sorted copy; ties fall back to the name so the order never jitters. */
export function sortFits(fits: readonly NodeFit[], order: SortOrder): NodeFit[] {
  const compare = COMPARATORS[order]
  return [...fits].sort((a, b) => compare(a, b) || byName(a, b))
}

/** The cheapest place to run one pod, or `undefined` if it fits nowhere. */
export function cheapest(fits: readonly NodeFit[]): NodeFit | undefined {
  return sortFits(
    fits.filter((fit) => Number.isFinite(fit.costPerPod)),
    'cost-per-pod-asc',
  )[0]
}

/**
 * Instances that pass the family filter. An instance is shown when its family is
 * selected; a category with no family selected shows nothing.
 */
export function filterInstances(
  instances: readonly Instance[],
  families: ReadonlySet<string>,
): Instance[] {
  return instances.filter((instance) => families.has(instance.family))
}

export function familiesByCategory<F extends { readonly id: string; readonly category: Category }>(
  families: readonly F[],
): Map<Category, F[]> {
  const grouped = new Map<Category, F[]>()
  for (const family of families) {
    const list = grouped.get(family.category) ?? []
    list.push(family)
    grouped.set(family.category, list)
  }
  return grouped
}
